import { IconDefinition, fak } from '@awesome.me/kit-da59d5031a/icons';

import sortBy from 'lodash/sortBy';
import DependentIcon from 'src/assets/images/level-of-assist-icons/dependent.svg';
import IndependentIcon from 'src/assets/images/level-of-assist-icons/independent.svg';
import PartialModerateIcon from 'src/assets/images/level-of-assist-icons/partial-moderate.svg';
import SetupCleanupIcon from 'src/assets/images/level-of-assist-icons/setup-cleanup.svg';
import SubstantialMaximalIcon from 'src/assets/images/level-of-assist-icons/substantial-maximal.svg';
import SupervisionTouchingIcon from 'src/assets/images/level-of-assist-icons/supervision-touching.svg';
import { CareProvided } from 'src/store/sageAdminApi';
import { AssistanceLevel } from 'src/store/sageCareServiceApi';

export interface AssistanceLevelOption {
    id: AssistanceLevel;
    displayName: string;
    /**
     * Either a FontAwesome `IconDefinition` or a string URL to an SVG asset.
     * Level-of-assistance icons are multi-color (which FontAwesome does not
     * support), so they are sourced from `src/assets/images/level-of-assist-icons/`.
     */
    icon?: IconDefinition | string;
    description: string;
}

export const AssistanceLevelIds = {
    Independent: 'INDEPENDENT',
    SetupCleanup: 'SETUP_CLEANUP',
    SupervisionTouch: 'SUPERVISION_TOUCH',
    ModerateAssist: 'MODERATE_ASSIST',
    SubstantialAssist: 'SUBSTANTIAL_ASSIST',
    Dependent: 'DEPENDENT',
} as const;

export const AssistanceLevelOptions: AssistanceLevelOption[] = [
    {
        id: AssistanceLevelIds.Independent,
        displayName: 'Independent',
        icon: IndependentIcon,
        description:
            'Resident safely completes the activity by themself with no assistance from a helper.',
    },
    {
        id: AssistanceLevelIds.SetupCleanup,
        displayName: 'Setup / clean up assistance',
        icon: SetupCleanupIcon,
        description:
            'Helper sets up or cleans up, resident completes activity. Helper assists only prior to or following the activity.',
    },
    {
        id: AssistanceLevelIds.SupervisionTouch,
        displayName: 'Supervision / touching assistance',
        icon: SupervisionTouchingIcon,
        description:
            'Helper provides verbal cues and/or touching/steadying and/or contact guard assistance as resident completes activity. Assistance may be intermittent or throughout the activity.',
    },
    {
        id: AssistanceLevelIds.ModerateAssist,
        displayName: 'Partial / moderate assistance',
        icon: PartialModerateIcon,
        description:
            'Helper does less than half the effort. The helper may lift, hold, or support the trunk or limbs.',
    },
    {
        id: AssistanceLevelIds.SubstantialAssist,
        displayName: 'Substantial / maximal assistance',
        icon: SubstantialMaximalIcon,
        description:
            'Helper does more than half the effort. The helper lifts or holds the trunk or limbs.',
    },
    {
        id: AssistanceLevelIds.Dependent,
        displayName: 'Dependent',
        icon: DependentIcon,
        description:
            'Helper does all of the effort, resident does none to complete the activity. Or, the assistance of two or more helpers is required for the resident to complete the activity.',
    },
];

export interface HelperLevelOption {
    id: string;
    displayName: string;
    description: string;
}

export const HelperLevelOptions: HelperLevelOption[] = [
    {
        id: 'HELPER_ONE',
        displayName: '1 helper',
        description: 'Totally dependent of',
    },
    {
        id: 'HELPER_TWO_RESIDENT',
        displayName: '2 or more helpers, resident assists',
        description: 'Assist of',
    },
    {
        id: 'HELPER_TWO',
        displayName: '2 or more helpers',
        description: 'Totally dependent of',
    },
];
/* assistance level id map */
export const IdToAssistanceLevelMap = new Map(AssistanceLevelOptions.map((opt) => [opt.id, opt]));

export interface SectionGgCategory {
    id: string;
    displayName?: string;
    sortOrder: number;
}
export interface SectionGgResolutionType {
    icon?: IconDefinition;
    id: string;
    displayName: string;
    sortOrder: number;
    category?: SectionGgCategory;
}
export interface SectionGgAdlCategory {
    id: string;
    displayName?: string;
    sortOrder: number;
    subTypes?: SectionGgResolutionType[];
}

export const SectionGgCategories: Record<string, SectionGgCategory> = {
    Request: {
        id: 'NON_EMERGENCY_REQUEST',
        displayName: 'Request',
        sortOrder: 1,
    },
    Emergency: {
        id: 'EMERGENCY',
        displayName: 'Emergency',
        sortOrder: 2,
    },
    SelfCareAdls: {
        id: 'SELF_CARE_ADLS',
        displayName: 'Self-Care ADLs',
        sortOrder: 3,
    },
    MobilityAdls: {
        id: 'MOBILITY_ADLS',
        displayName: 'Mobility ADLs',
        sortOrder: 4,
    },
    OtherHelp: {
        id: 'OTHER_HELP',
        displayName: 'Other Help',
        sortOrder: 5,
    },
};

export const SectionGgResolutionTypeIds: Record<string, CareProvided> = {
    Fall: 'EMERGENCY_FALL',
    OtherEmergency: 'EMERGENCY_OTHER',
    Medtech: 'REQUEST_MEDTECH',
    EatingFeeding: 'ADL_EATING',
    OralHygiene: 'ADL_ORAL_HYGIENE',
    Toileting: 'ADL_TOILETING',
    UpperBodyDressing: 'ADL_DRESSING_UPPER_BODY',
    LowerBodyDressing: 'ADL_DRESSING_LOWER_BODY',
    Footwear: 'ADL_DRESSING_FOOTWEAR',
    PersonalHygiene: 'ADL_PERSONAL_HYGIENE',
    Roll: 'ADL_MOBILITY_ROLL',
    SitToLying: 'ADL_MOBILITY_SIT_TO_LYING',
    LayingToSitting: 'ADL_MOBILITY_LYING_TO_SIT',
    SitToStand: 'ADL_MOBILITY_SIT_TO_STAND',
    ChairTransfer: 'ADL_TRANSFER_TO_CHAIR',
    ToiletTransfer: 'ADL_TRANSFER_TOILET',
    TubShowerTransfer: 'ADL_TRANSFER_SHOWER',
    CarTransfer: 'ADL_TRANSFER_CAR',
    PickingUpObject: 'ADL_MOBILITY_PICK_UP_OBJECT',
    EmotionalSupport: 'REQUEST_EMOTIONAL_SUPPORT',
    HousekeepingUnspecified: 'HOUSEKEEPING_UNSPECIFIED',
    Maintenance: 'REQUEST_MAINTENANCE',
    OtherRequest: 'REQUEST_OTHER',
    AccidentallyPressed: 'NON_EMERGENCY_ACCIDENTAL_PRESS',
};

export const SectionGgResolutionTypes: Record<string, SectionGgResolutionType> = {
    Fall: {
        icon: fak.faFall,
        id: SectionGgResolutionTypeIds.Fall,
        displayName: 'Fall',
        sortOrder: 1,
        category: SectionGgCategories.Emergency,
    },
    OtherEmergency: {
        icon: fak.faEmergency,
        id: SectionGgResolutionTypeIds.OtherEmergency,
        displayName: 'Other Emergency',
        sortOrder: 2,
        category: SectionGgCategories.Emergency,
    },
    Medtech: {
        icon: fak.faNurse,
        id: SectionGgResolutionTypeIds.Medtech,
        displayName: 'Medtech',
        sortOrder: 1,
        category: SectionGgCategories.Request,
    },
    EatingFeeding: {
        icon: fak.faEating,
        id: SectionGgResolutionTypeIds.EatingFeeding,
        displayName: 'Eating / Feeding',
        sortOrder: 1,
        category: SectionGgCategories.SelfCareAdls,
    },
    OralHygiene: {
        icon: fak.faOralHygiene,
        id: SectionGgResolutionTypeIds.OralHygiene,
        displayName: 'Oral Hygiene',
        sortOrder: 2,
        category: SectionGgCategories.SelfCareAdls,
    },
    Toileting: {
        icon: fak.faToileting,
        id: SectionGgResolutionTypeIds.Toileting,
        displayName: 'Toileting Hygiene',
        sortOrder: 3,
        category: SectionGgCategories.SelfCareAdls,
    },
    UpperBodyDressing: {
        icon: fak.faDressing,
        id: SectionGgResolutionTypeIds.UpperBodyDressing,
        displayName: 'Upper Body Dressing',
        sortOrder: 5,
        category: SectionGgCategories.SelfCareAdls,
    },
    LowerBodyDressing: {
        icon: fak.faLowerBodyDressing,
        id: SectionGgResolutionTypeIds.LowerBodyDressing,
        displayName: 'Lower Body Dressing',
        sortOrder: 6,
        category: SectionGgCategories.SelfCareAdls,
    },
    Footwear: {
        icon: fak.faFootwear,
        id: SectionGgResolutionTypeIds.Footwear,
        displayName: 'Putting On/Taking Off Footwear',
        sortOrder: 7,
        category: SectionGgCategories.SelfCareAdls,
    },
    PersonalHygiene: {
        icon: fak.faGrooming,
        id: SectionGgResolutionTypeIds.PersonalHygiene,
        displayName: 'Personal Hygiene',
        sortOrder: 8,
        category: SectionGgCategories.SelfCareAdls,
    },
    Roll: {
        icon: fak.faRollLeftRight,
        id: SectionGgResolutionTypeIds.Roll,
        displayName: 'Roll Left and Right',
        sortOrder: 1,
        category: SectionGgCategories.MobilityAdls,
    },
    SitToLying: {
        icon: fak.faSitToLying,
        id: SectionGgResolutionTypeIds.SitToLying,
        displayName: 'Sit to Lying',
        sortOrder: 2,
        category: SectionGgCategories.MobilityAdls,
    },
    LayingToSitting: {
        icon: fak.faLayingToSittingBed,
        id: SectionGgResolutionTypeIds.LayingToSitting,
        displayName: 'Laying to Sitting on Side of Bed',
        sortOrder: 3,
        category: SectionGgCategories.MobilityAdls,
    },
    SitToStand: {
        icon: fak.faSitToStand,
        id: SectionGgResolutionTypeIds.SitToStand,
        displayName: 'Sit to Stand',
        sortOrder: 4,
        category: SectionGgCategories.MobilityAdls,
    },
    ChairTransfer: {
        icon: fak.faTransfer,
        id: SectionGgResolutionTypeIds.ChairTransfer,
        displayName: 'Chair/Bed-To-Chair Transfer',
        sortOrder: 5,
        category: SectionGgCategories.MobilityAdls,
    },
    ToiletTransfer: {
        icon: fak.faToileting,
        id: SectionGgResolutionTypeIds.ToiletTransfer,
        displayName: 'Toilet Transfer',
        sortOrder: 6,
        category: SectionGgCategories.MobilityAdls,
    },
    TubShowerTransfer: {
        icon: fak.faTubShowerTransfer,
        id: SectionGgResolutionTypeIds.TubShowerTransfer,
        displayName: 'Tub/Shower Transfer',
        sortOrder: 7,
        category: SectionGgCategories.MobilityAdls,
    },
    CarTransfer: {
        icon: fak.faCarTransfer,
        id: SectionGgResolutionTypeIds.CarTransfer,
        displayName: 'Car Transfer',
        sortOrder: 8,
        category: SectionGgCategories.MobilityAdls,
    },
    PickingUpObject: {
        icon: fak.faPickingUpObject,
        id: SectionGgResolutionTypeIds.PickingUpObject,
        displayName: 'Picking Up Object',
        sortOrder: 12,
        category: SectionGgCategories.MobilityAdls,
    },
    EmotionalSupport: {
        icon: fak.faEmotionalSupport,
        id: SectionGgResolutionTypeIds.EmotionalSupport,
        displayName: 'Emotional Support',
        sortOrder: 1,
        category: SectionGgCategories.OtherHelp,
    },
    HousekeepingUnspecified: {
        icon: fak.faHousekeeping1,
        id: SectionGgResolutionTypeIds.HousekeepingUnspecified,
        displayName: 'Housekeeping',
        sortOrder: 2,
        category: SectionGgCategories.OtherHelp,
    },
    Maintenance: {
        icon: fak.faRepair,
        id: SectionGgResolutionTypeIds.Maintenance,
        displayName: 'Maintenance',
        sortOrder: 3,
        category: SectionGgCategories.OtherHelp,
    },
    OtherRequest: {
        icon: fak.faQuestion,
        id: SectionGgResolutionTypeIds.OtherRequest,
        displayName: 'Other Request',
        sortOrder: 4,
        category: SectionGgCategories.OtherHelp,
    },
    AccidentallyPressed: {
        icon: fak.faAccidentlyPressed,
        id: SectionGgResolutionTypeIds.AccidentallyPressed,
        displayName: 'Accidentally Pressed',
        sortOrder: 5,
        category: SectionGgCategories.OtherHelp,
    },
};

/** Builds subTypes from possibly-undefined refs; skips missing keys so a rename won't crash the app. */
function subTypesFor(...items: (SectionGgResolutionType | undefined)[]): SectionGgResolutionType[] {
    return sortBy(
        items.filter((type): type is SectionGgResolutionType => type != null),
        'sortOrder'
    );
}

export const SectionGgAdls: SectionGgAdlCategory[] = [
    {
        id: SectionGgCategories.Request.id,
        sortOrder: SectionGgCategories.Request.sortOrder,
        subTypes: [
            {
                ...SectionGgResolutionTypes.Medtech,
                displayName: 'Needs Nurse',
                icon: fak.faNurse,
                category: undefined,
            },
        ],
    },
    {
        ...SectionGgCategories.Emergency,
        subTypes: subTypesFor(
            SectionGgResolutionTypes.Fall,
            SectionGgResolutionTypes.OtherEmergency
        ),
    },
    {
        ...SectionGgCategories.SelfCareAdls,
        subTypes: subTypesFor(
            SectionGgResolutionTypes.EatingFeeding,
            SectionGgResolutionTypes.OralHygiene,
            SectionGgResolutionTypes.Toileting,
            SectionGgResolutionTypes.UpperBodyDressing,
            SectionGgResolutionTypes.LowerBodyDressing,
            SectionGgResolutionTypes.Footwear,
            SectionGgResolutionTypes.PersonalHygiene
        ),
    },
    {
        ...SectionGgCategories.MobilityAdls,
        subTypes: subTypesFor(
            SectionGgResolutionTypes.Roll,
            SectionGgResolutionTypes.SitToLying,
            SectionGgResolutionTypes.LayingToSitting,
            SectionGgResolutionTypes.SitToStand,
            SectionGgResolutionTypes.ChairTransfer,
            SectionGgResolutionTypes.ToiletTransfer,
            SectionGgResolutionTypes.TubShowerTransfer,
            SectionGgResolutionTypes.CarTransfer,
            SectionGgResolutionTypes.PickingUpObject
        ),
    },
    {
        ...SectionGgCategories.OtherHelp,
        subTypes: subTypesFor(
            SectionGgResolutionTypes.EmotionalSupport,
            SectionGgResolutionTypes.HousekeepingUnspecified,
            SectionGgResolutionTypes.Maintenance,
            SectionGgResolutionTypes.OtherRequest,
            SectionGgResolutionTypes.AccidentallyPressed
        ),
    },
];

/* Map from ADL option ID to its option object */
export const IdToSkilledNursingAdlOptionMap = new Map(
    Object.values(SectionGgResolutionTypes).map((type) => [type.id, type])
);
